import {
  ExternalCollection,
  FetchParams,
} from "@bitwarden/web-vault/app/services/store/external.collection.abstraction";
import { ExternalView } from "@bitwarden/web-vault/app/models/view/external.view";
import { inject, signal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { IReferenceData } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";

import {
  australianSuperPluginConfig,
  buildGraphQLAuthPayloadLiteral,
} from "./australian-super.plugin-config";
import { PluginConfigService } from "./plugin-config.service";
import { resolvePluginConfigForFetch } from "./plugin-fetch-config";

interface ReferencesSelector {
  symbol?: { code?: string; name?: string; currency?: string };
  date?: string;
  rate?: number;
}

interface AuthResponse {
  auth: {
    response: string;
    identifier: string;
  };
}

interface AccountResponse {
  account: {
    id: string;
    name: string;
    balance: number;
    currency: string;
  };
}

interface TransactionsResponse {
  transaction: {
    info: {
      nextCursor: string;
      hasNext: boolean;
    };
  };
  nodes: {
    accountId: string;
    transactionId: string;
    transactionTime: string;
    amount: number;
    currency: string;
    description: string;
  };
}
export type TransactionOption = {
  accountId?: string;
  startDate: string;
  endDate: string;
};

interface FetchResponse<T> {
  nodes: T[];
  info?: { hasNext?: boolean; cursor?: string };
  total?: number;
}

interface AccountsParams {
  id?: string;
}

interface FetchCurrencyParams<T extends object> extends FetchParams {
  config: string;
  type: GraphQLQuery;
  limit?: number;
  cursor?: string;
  inputs: T;
  /** Overrides the vault/static apiKey sent as `Authorization: Bearer` for this request. */
  apiKey?: string;
}

function isAccountsParams(params: FetchParams): params is FetchCurrencyParams<AccountsParams> {
  return params.type === "accounts";
}

type GraphQLQuery = "transactions" | "accounts" | "auth";

interface GraphQLResponse<T> {
  data: {
    [key in GraphQLQuery]: FetchResponse<T>;
  };
}

export type SymbolView = { code: string; name: string };

export interface PluginFormField {
  field: string;
  label: string;
  type: "text" | "number" | "checkbox" | "select" | "password";
  options?: string[];
  required: boolean;
  defaultValue?: any;
}

export interface PluginConfig {
  id: string;
  name: string;
  url: string;
  apiKey?: string;
  fields?: PluginFormField[];
}

/**
 * Plugin Configurations
 *
 * The `name` field must match the `id` field in the corresponding
 * PluginMetadata in models/data/plugin-metadata.ts
 *
 * For example:
 * - PluginConfig { name: "opal-card", ... }
 * - PluginMetadata { id: "opal-card", ... }
 */

const pluginConfigs: PluginConfig[] = [
  {
    id: "1",
    name: "blockstream",
    url: "https://new.plugin.gloss-vault.com/blockstream",
    fields: [
      {
        field: "id",
        label: "address",
        type: "text",
        required: true,
        defaultValue: "",
      },
    ],
  },
  {
    id: "3",
    name: "ethvm",
    url: "https://new.plugin.gloss-vault.com/ethvm",
    fields: [
      {
        field: "id",
        label: "address",
        type: "text",
        required: true,
        defaultValue: "",
      },
    ],
  },
  {
    id: "4",
    name: "solana",
    url: "https://new.plugin.gloss-vault.com/solana",
    fields: [
      {
        field: "id",
        label: "address",
        type: "text",
        required: true,
        defaultValue: "",
      },
    ],
  },
  {
    id: "2",
    name: "opal-card",
    url: "https://new.plugin.gloss-vault.com/opal-card",
    fields: [
      {
        field: "id",
        label: "username",
        type: "text",
        required: true,
        defaultValue: "",
      },
      {
        field: "password",
        label: "password",
        type: "password",
        required: true,
        defaultValue: "",
      },
    ],
  },
  { id: "5", name: "gift-card", url: "http://localhost:4043" },
  australianSuperPluginConfig,
];

export const getConfigs = (): PluginConfig[] => {
  return pluginConfigs;
};

export class ExternalBookTransactionStoreCollection extends ExternalCollection {
  private log: LogService = inject(LogService);
  private pluginConfigService = inject(PluginConfigService);
  // TODO: will change it to SymbolView once it is abled to save into vault
  private _transactions = signal<any>(null);
  readonly transactions = this._transactions.asReadonly();
  private _accounts = signal<any>(null);
  readonly accounts = this._accounts.asReadonly();
  private _user = signal<any>(null);
  readonly user = this._user.asReadonly();
  private token: string | null = null;
  readonly configs = getConfigs();

  clear() {
    this._transactions.set([]);
    this._accounts.set(null);
  }

  async fetch(params: FetchCurrencyParams<any>) {
    const vaultPlugins = this.pluginConfigService.getConfigsByType("plugin");
    const resolved = resolvePluginConfigForFetch(params.config, this.configs, vaultPlugins);

    if (!resolved) {
      return [];
    }

    // Legacy plugins authenticate before their credentials (and apiKey) are in the vault, so
    // the caller passes the apiKey it will save (#5510).
    const config = params.apiKey ? { ...resolved, apiKey: params.apiKey } : resolved;

    if (params.type === "auth") {
      await this.authenticate(config, params.inputs);
      return this.user();
    }

    if (params.type === "transactions") {
      await this.fetchTransactions(config, params.inputs);
      return this.transactions();
    }

    if (isAccountsParams(params)) {
      await this.fetchAccounts(config, params.inputs);
      return this.accounts();
    }
  }

  triggerChanges(): void {}

  protected mapToView(data: any): ExternalView {
    throw new Error("Method not implemented.");
  }

  private async fetchTransactions(
    config: PluginConfig,
    inputs: {
      id: string;
      accountId: string;
      startDate: string;
      endDate: string;
      cursor: string;
      limit: number;
    },
  ) {
    const query = `
      query GetTransactions($identifier: String, $options: TransactionOption, $limit: Int, $cursor: ID) {
        transaction(identifier: $identifier, options: $options, limit: $limit, cursor: $cursor) {
          info {
            nextCursor
            hasNext
          }
          nodes {
            accountId
            transactionId
            transactionTime
            amount
            currency
            description
            status
            balance
          }
        }
      }
    `;

    try {
      const { data } = await this.query<{ data: TransactionsResponse }>(
        config.url,
        query,
        config.apiKey,
        {
          identifier: inputs.id,
          options: {
            accountId: (inputs.accountId ?? "").replace(/\s/g, ""),
            startDate: inputs.startDate,
            endDate: inputs.endDate,
          },
          cursor: inputs.cursor,
          limit: inputs.limit,
        },
      );

      this._transactions.set(data);
      this.log.debug(`Fetched transactions data: ${JSON.stringify(data)}`);
    } catch (error) {
      this.log.error(`Error fetching symbols: ${error.toString()}`);
      throw error;
    }
  }

  private async authenticate(config: PluginConfig, args: Record<string, unknown>) {
    const payload = buildGraphQLAuthPayloadLiteral(args);
    const query = `
    mutation {
    auth(payload: { ${payload} }) {
    response
    identifier
      }
    }`;

    try {
      const { data } = await this.query<{ data: AuthResponse }>(config.url, query, config.apiKey);
      this.log.debug(`Authentication response: ${JSON.stringify(data)}`);
      this._user.set(data.auth);
      return data;
    } catch (error) {
      this.log.error(`Error authenticating: ${error.toString()}`);
      throw error;
    }
  }

  private async fetchAccounts(config: PluginConfig, args: { id?: string }) {
    const query = `
        query Account($identifier: String) {
          account(identifier: $identifier) {
            id
            name
            balance
            currency
          }
        }
      `;

    try {
      const { data } = await this.query<{ data: AccountResponse }>(
        config.url,
        query,
        config.apiKey,
        {
          identifier: args.id,
        },
      );
      this._accounts.set(data);
      this.log.debug(`Fetched accounts data: ${JSON.stringify(data)}`);
    } catch (error) {
      this.log.error(`Error fetching references: ${error.toString()}`);
      throw error;
    }
  }

  private async auth(url: string, apiKey?: string) {
    const q = `
      query { auth(apiKey: "${apiKey}") }
    `;
    return await this.query(url, q, apiKey);
  }

  private async query<T>(url: string, q: string, token: string, variables: any = {}): Promise<T> {
    const headers: { "Content-Type": string; Authorization?: string } = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const options = {
      url,
      method: "POST",
      headers,
      data: JSON.stringify({ query: q, variables }),
    };

    const response = await capFetch(options);

    if (response.status !== 200) {
      throw new Error("Network response was not ok");
    }

    const body = response.data as { errors?: { message?: string }[] };
    if (body?.errors?.length) {
      const msg = body.errors
        .map((e) => e?.message ?? "")
        .filter(Boolean)
        .join("; ");
      throw new Error(msg || "GraphQL request failed");
    }

    return response.data;
  }
}
